import React, { FC } from 'react'
import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material'
import { useFormik, FormikProvider, Form } from 'formik'
import { useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { billingStatsFilterResetKeys } from 'src/components/sections/sensor/sensorBillingStats/billingStatsRows'

const activationDateOptions = [
  { name: 'All sensors', value: 'all' },
  { name: 'Activated more than 24 months ago', value: '24' }
]

const lastPingOptions = [
  { name: 'All sensors', value: 'all' },
  { name: 'More than 3 months ago', value: '3' },
  { name: 'More than 6 months ago', value: '6' },
  { name: 'More than 9 months ago', value: '9' }
]

const activationStatusOptions = [
  { name: 'All', value: 'all' },
  { name: 'Active', value: 'active' },
  { name: 'Deactive', value: 'deactive' }
]

// UI "Both" maps to the API `all` value; the list defaults to production (see ClimitBillingSensorsList).
const environmentOptions = [
  { name: 'Both', value: 'all' },
  { name: 'Staging', value: 'staging' },
  { name: 'Production', value: 'production' }
]

export const defaultBillingEnvironment = 'production'

export const BillingSensorsFilters: FC = () => {
  const [queryParams, setQueryParams] = useQueryParams()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [SearchParamsKeys.ActivatedMoreThanMonths]: queryParams[SearchParamsKeys.ActivatedMoreThanMonths] || 'all',
      [SearchParamsKeys.NoPingMoreThanMonths]: queryParams[SearchParamsKeys.NoPingMoreThanMonths] || 'all',
      [SearchParamsKeys.ActivationStatus]: queryParams[SearchParamsKeys.ActivationStatus] || 'all',
      [SearchParamsKeys.Environment]: queryParams[SearchParamsKeys.Environment] || defaultBillingEnvironment
    },
    onSubmit: async (formState) => {
      const activatedMoreThanMonths = formState[SearchParamsKeys.ActivatedMoreThanMonths] || 'all'
      const noPingMoreThanMonths = formState[SearchParamsKeys.NoPingMoreThanMonths] || 'all'
      const activationStatus = formState[SearchParamsKeys.ActivationStatus] || 'all'
      const environment = formState[SearchParamsKeys.Environment] || defaultBillingEnvironment
      await setQueryParams(
        {
          ...(activatedMoreThanMonths !== 'all' ? { [SearchParamsKeys.ActivatedMoreThanMonths]: activatedMoreThanMonths } : {}),
          ...(noPingMoreThanMonths !== 'all' ? { [SearchParamsKeys.NoPingMoreThanMonths]: noPingMoreThanMonths } : {}),
          ...(activationStatus !== 'all' ? { [SearchParamsKeys.ActivationStatus]: activationStatus } : {}),
          [SearchParamsKeys.Environment]: environment
        },
        billingStatsFilterResetKeys
      )
    }
  })

  const { submitForm, values, setFieldValue } = formik

  const handleActivationStatusChanges = (event: SelectChangeEvent<typeof values['activationStatus']>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.ActivationStatus, value)
    submitForm()
  }

  const handleEnvironmentChanges = (event: SelectChangeEvent<typeof values['environment']>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.Environment, value)
    submitForm()
  }

  const handleActivationDateChanges = (event: SelectChangeEvent<typeof values['activatedMoreThanMonths']>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.ActivatedMoreThanMonths, value)
    submitForm()
  }

  const handleLastPingChanges = (event: SelectChangeEvent<typeof values['noPingMoreThanMonths']>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.NoPingMoreThanMonths, value)
    submitForm()
  }

  return (
    <FormikProvider value={formik}>
      <Form>
        <Grid container alignItems="center" spacing={{ xs: 0, sm: 2 }}>
          <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 1, width: { xs: '100%', sm: 400 } }}>
              <InputLabel id="environment-chip-label">Environment</InputLabel>
              <Select
                labelId="environment-chip-label"
                id="environment-chip"
                value={values[SearchParamsKeys.Environment] || defaultBillingEnvironment}
                onChange={handleEnvironmentChanges}
                input={<OutlinedInput id="environment-select-chip" label="Environment" />}
                renderValue={(selected) => {
                  const item = environmentOptions.find((o) => o.value === selected)
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip key={item?.value} label={item?.name} />
                    </Box>
                  )
                }}
              >
                {environmentOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 1, width: 400 }}>
              <InputLabel id="activation-status-chip-label">Activation status</InputLabel>
              <Select
                labelId="activation-status-chip-label"
                id="activation-status-chip"
                value={values[SearchParamsKeys.ActivationStatus] || 'all'}
                onChange={handleActivationStatusChanges}
                input={<OutlinedInput id="activation-status-select-chip" label="Activation status" />}
                renderValue={(selected) => {
                  const item = activationStatusOptions.find((o) => o.value === selected)
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip key={item?.value} label={item?.name} />
                    </Box>
                  )
                }}
              >
                {activationStatusOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 1, width: { xs: '100%', sm: 400 } }}>
              <InputLabel id="activation-date-chip-label">Activation date</InputLabel>
              <Select
                labelId="activation-date-chip-label"
                id="activation-date-chip"
                value={values[SearchParamsKeys.ActivatedMoreThanMonths] || 'all'}
                onChange={handleActivationDateChanges}
                input={<OutlinedInput id="activation-date-select-chip" label="Activation date" />}
                renderValue={(selected) => {
                  const item = activationDateOptions.find((o) => o.value === selected)
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip key={item?.value} label={item?.name} />
                    </Box>
                  )
                }}
              >
                {activationDateOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 1, width: { xs: '100%', sm: 400 } }}>
              <InputLabel id="last-ping-time-chip-label">Last ping time</InputLabel>
              <Select
                labelId="last-ping-time-chip-label"
                id="last-ping-time-chip"
                value={values[SearchParamsKeys.NoPingMoreThanMonths] || 'all'}
                onChange={handleLastPingChanges}
                input={<OutlinedInput id="last-ping-time-select-chip" label="Last ping time" />}
                renderValue={(selected) => {
                  const item = lastPingOptions.find((o) => o.value === selected)
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip key={item?.value} label={item?.name} />
                    </Box>
                  )
                }}
              >
                {lastPingOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  )
}
